import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/**
 * Status colors follow docs/15 §6: success/warning/info/destructive render as
 * tinted badges (solid green is reserved for the primary action); `live` is the
 * one solid-green exception — Green 500 fill + navy ink (9.06:1) for LIVE/OPEN.
 */
const badgeVariants = cva(
  // No `whitespace-nowrap` here: the base is shared with free-text chips (company
  // names, role lists) that must be allowed to wrap. `StatusBadge` adds it for the
  // single-token status pills that need it.
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        neutral: 'border-transparent bg-muted text-muted-foreground',
        outline: 'text-foreground',
        success: 'border-transparent bg-success-tint text-success-tint-fg',
        warning: 'border-transparent bg-warning-tint text-warning-tint-fg',
        info: 'border-transparent bg-info-tint text-info-tint-fg',
        destructive: 'border-transparent bg-danger-tint text-danger-tint-fg',
        live: 'border-transparent bg-green-500 text-navy-800',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}
